import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { closeModal } from "libs/modals";
import { useTrackEvent } from "libs/analytics";
import {
  UNSAVED_POST_RESTORE_DIALOG_SHOWN,
  UNSAVED_POST_RESTORE_ACTION,
} from "constants/Events";
import { restoreDrafts } from "./bulk-create-post.actions";
import { clearDraftsFromStorage, PostFormDraft } from "./draft-storage";
import { DRAFT_RESTORATION_MODAL } from "./bulk-create-post.model";

interface DraftRestorationModalProps {
  drafts: PostFormDraft[];
}

interface DraftMetadataRowProps {
  label: string;
  value: string;
}

function DraftMetadataRow({ label, value }: DraftMetadataRowProps) {
  return (
    <div className="flex items-center gap-1 font-inter text-sm leading-5 text-neutral-600">
      <span className="font-semibold">{label}:</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

/**
 * Format timestamp to relative time string
 */
function formatTimestamp(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;

  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  return new Date(timestamp).toLocaleDateString();
}

/**
 * Draft Restoration Modal
 *
 * Shown on app load when unsaved post drafts are detected
 * Displays metadata about each draft and allows user to restore or discard
 */
export function DraftRestorationModal({ drafts }: DraftRestorationModalProps) {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const metadata = drafts.map((d) => d.metadata);

  // Get the most recent draft timestamp
  const mostRecentTimestamp = Math.max(...metadata.map((m) => m.timestamp));

  // Track when dialog is shown - only once per mount
  useEffect(() => {
    trackEvent({
      eventName: UNSAVED_POST_RESTORE_DIALOG_SHOWN,
      metadata: { unsavedPostCount: drafts.length },
    });
  }, [trackEvent, drafts.length]);

  const handleRestore = () => {
    trackEvent({
      eventName: UNSAVED_POST_RESTORE_ACTION,
      metadata: { action: "restore", unsavedPostCount: drafts.length },
    });
    dispatch(restoreDrafts({ drafts }));
    dispatch(closeModal({ type: DRAFT_RESTORATION_MODAL }));
  };

  const handleDiscard = () => {
    trackEvent({
      eventName: UNSAVED_POST_RESTORE_ACTION,
      metadata: { action: "discard", unsavedPostCount: drafts.length },
    });
    clearDraftsFromStorage();
    dispatch(closeModal({ type: DRAFT_RESTORATION_MODAL }));
  };

  const handleClose = () => {
    dispatch(closeModal({ type: DRAFT_RESTORATION_MODAL }));
  };

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="max-w-2xl p-0"
        contentWrapperClassName="px-0"
        showCloseButton={false}
      >
        <DialogClose asChild>
          <button
            type="button"
            aria-label="Close"
            className="absolute top-4 right-4 z-10 flex size-5 items-center justify-center text-neutral-500 transition-colors hover:text-neutral-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-pointer"
          >
            <i className="fa-regular fa-xmark text-base" />
          </button>
        </DialogClose>
        <DialogHeader className="gap-1 border-b border-neutral-200 px-6 py-4 pt-4">
          <DialogTitle className="text-[24px] leading-8">
            Unsaved posts
          </DialogTitle>
          <DialogDescription className="font-inter text-sm leading-5 text-neutral-600">
            You have {drafts.length} unsaved post
            {drafts.length > 1 ? "s" : ""} from{" "}
            {formatTimestamp(mostRecentTimestamp)}. Would you like to restore
            your work?
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="max-h-[400px] px-6 py-4 pb-4">
          <div className="space-y-2">
            {metadata.map((meta) => (
              <div
                key={meta.postId}
                className="rounded-lg border border-neutral-200 bg-neutral-25 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="font-inter text-sm font-semibold leading-5 text-neutral-600">
                      {meta.requestType === "create" ? "New post" : "Edit post"}
                    </div>

                    {meta.accountNames.length > 0 && (
                      <DraftMetadataRow
                        label="Accounts"
                        value={meta.accountNames.join(", ")}
                      />
                    )}

                    {meta.platformNames.length > 0 && (
                      <DraftMetadataRow
                        label="Platforms"
                        value={meta.platformNames.join(", ")}
                      />
                    )}

                    <div className="space-y-1">
                      {meta.mediaCount > 0 && (
                        <div className="flex items-center gap-1 font-inter text-sm leading-5 text-neutral-600">
                          <i className="fa-regular fa-image w-4 text-center text-[12px]" />
                          <span>
                            {meta.mediaCount} media asset
                            {meta.mediaCount > 1 ? "s" : ""}
                          </span>
                        </div>
                      )}

                      {meta.caption && (
                        <div className="flex w-full min-w-0 items-center gap-1 font-inter text-sm leading-5 text-neutral-600">
                          <i className="fa-regular fa-align-left w-4 text-center text-[12px]" />
                          <span className="min-w-0 truncate italic">
                            &quot;{meta.caption}&quot;
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="ml-3 whitespace-nowrap font-inter text-sm leading-5 text-neutral-500">
                    {formatTimestamp(meta.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogBody>

        <DialogFooter className="flex-row justify-end border-t border-neutral-200 px-6 py-4">
          <Button variant="destructive-secondary" onClick={handleDiscard}>
            Discard all
          </Button>
          <Button variant="blue-primary" onClick={handleRestore}>
            Restore posts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
